/**
 * LA LECTURE DU CATALOGUE : NOMS, RECHERCHE, FILTRES ET TRI (LOT F4).
 * ============================================================================
 *
 * Fonctions pures, sur les types servis par la route 1. Chaque mot vient des
 * messages du module : la mesure se nomme par son code servi (`variable`),
 * jamais par le code lui-même ; un code inconnu prend « Autre mesure ».
 * La recherche (S:6117-6145) ignore la casse et les accents et exige chaque
 * mot ; le tri par usage de la maquette n'est pas servi par la façade.
 */
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import type { AlarmesV2TypeAlarme } from "@modules/alarmes-v2/types/alarmes-v2"

/** Une clé propre de l'objet, jamais une clé héritée (`constructor`…). */
const possede = (o: object, cle: string): boolean =>
    Object.prototype.hasOwnProperty.call(o, cle)

/** Les filtres de la page. */
export interface FiltresCatalogue {
    texte: string
    /** Le nom de la mesure, ou `tous`. */
    mesure: string
    /** `building`, `source` ou `tous`. */
    equipement: string
    tri: "alpha" | "a_traiter"
}

/** Les filtres de départ. */
export const FILTRES_DE_DEPART: FiltresCatalogue = {
    texte: "",
    mesure: "tous",
    equipement: "tous",
    tri: "alpha",
}

/** Les types d'équipement que l'écran sait nommer. */
export const EQUIPEMENTS = ["building", "source"] as const

/** Sans accent, en minuscules. */
export function sansAccentMin(texte: string): string {
    return texte.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

const chaine = (v: unknown): string =>
    typeof v === "string" && v !== "" ? v : ""

/**
 * Le nom d'un type : celui de la liste blanche, sinon celui de ses textes,
 * sinon une phrase. Jamais le code.
 */
export function nomDuTypeDuCatalogue(
    type: AlarmesV2TypeAlarme,
    t: Traduire
): string {
    return (
        chaine(type.nom) ||
        chaine(type.verbatims?.nom) ||
        t("alarmesV2.catalogue.carte.nomNonDisponible")
    )
}

/** Le nom de la mesure surveillée par un type. */
export function mesureDuType(type: AlarmesV2TypeAlarme, t: Traduire): string {
    const mesures = MESSAGES_ALARMES_V2.mesures as Record<string, string>
    const v = chaine(type.variable)
    return v && v !== "inconnue" && possede(mesures, v)
        ? t(`alarmesV2.mesures.${v}`)
        : t("alarmesV2.mesures.inconnue")
}

/** « bâtiment ou compteur ou capteur » : où le type se pose. */
export function equipementsDuType(
    type: AlarmesV2TypeAlarme,
    t: Traduire
): string {
    const genres = Array.isArray(type.objet_types) ? type.objet_types : []
    const noms = genres.map((g) =>
        (EQUIPEMENTS as readonly string[]).includes(g)
            ? t(`alarmesV2.catalogue.equipements.${g}`)
            : t("alarmesV2.catalogue.equipementInconnu")
    )
    const uniques = [...new Set(noms)]
    return uniques.length > 0
        ? uniques.join(t("alarmesV2.catalogue.ou"))
        : t("alarmesV2.catalogue.equipementInconnu")
}

/** Vrai pour un type pour information (D103) ; tout autre se lit « à traiter ». */
export function estPourInformation(type: AlarmesV2TypeAlarme): boolean {
    return type.gravite === "pour_information"
}

/** Les mesures des types servis, une fois chacune, dans l'ordre alphabétique. */
export function mesuresDuCatalogue(
    types: AlarmesV2TypeAlarme[],
    t: Traduire
): string[] {
    return [...new Set(types.map((ty) => mesureDuType(ty, t)))].sort((a, b) =>
        a.localeCompare(b, "fr")
    )
}

/**
 * Le compte de ce qui n'est pas proposé, phrase par phrase.
 *
 * Sans équipement désigné, rien n'a été compté : aucun nombre n'est dit.
 * Sur un équipement désigné, un compteur `null` (servi illisible) se dit
 * « nous n'avons pas pu lire », jamais « sans filtre » ni rien.
 * @param ecartes - `types_ecartes` lu, ou `null`
 * @param nonRattaches - `types_non_rattaches` lu, ou `null`
 * @param surUnEquipement - vrai si la lecture portait sur un équipement
 * @param t - la traduction
 */
export function comptesDuFiltre(
    ecartes: number | null,
    nonRattaches: number | null,
    surUnEquipement: boolean,
    t: Traduire
): string[] {
    if (!surUnEquipement && ecartes === null && nonRattaches === null)
        return [t("alarmesV2.catalogue.nonFiltre")]
    const phrases: string[] = []
    if (ecartes !== null && ecartes > 0)
        phrases.push(
            ecartes === 1
                ? t("alarmesV2.catalogue.ecartesUn")
                : t("alarmesV2.catalogue.ecartes", { n: ecartes })
        )
    if (nonRattaches !== null && nonRattaches > 0)
        phrases.push(
            nonRattaches === 1
                ? t("alarmesV2.catalogue.nonRattachesUn")
                : t("alarmesV2.catalogue.nonRattaches", { n: nonRattaches })
        )
    if (surUnEquipement && (ecartes === null || nonRattaches === null))
        phrases.push(t("alarmesV2.catalogue.compteInconnu"))
    return phrases
}

/**
 * Ce que dit un catalogue sans aucun type montré.
 *
 * `rien_publie` seulement si l'on sait que rien n'a été écarté (ou que rien
 * n'était filtré) ; tout écarté se dit par le compte (`null` : rien de plus) ;
 * sur un équipement dont le compte d'écartés est illisible, `rien_propose`.
 * @param total - le nombre de types montrables servis
 * @param ecartes - `types_ecartes` lu, ou `null`
 * @param surUnEquipement - vrai si la lecture portait sur un équipement
 */
export function catalogueVide(
    total: number,
    ecartes: number | null,
    surUnEquipement: boolean
): "rien_publie" | "rien_propose" | null {
    if (total > 0) return null
    if (ecartes !== null) return ecartes > 0 ? null : "rien_publie"
    return surUnEquipement ? "rien_propose" : "rien_publie"
}

/** Le texte où cherche la recherche. */
function texteCherche(type: AlarmesV2TypeAlarme, t: Traduire): string {
    return sansAccentMin(
        [
            nomDuTypeDuCatalogue(type, t),
            mesureDuType(type, t),
            chaine(type.verbatims?.phrase),
            chaine(type.verbatims?.si_personne_n_agit),
            equipementsDuType(type, t),
        ].join(" ")
    )
}

/**
 * Les types à afficher, filtrés et triés.
 * @param types - les types servis
 * @param f - les filtres de la page
 * @param t - la traduction
 */
export function typesAffiches(
    types: AlarmesV2TypeAlarme[],
    f: FiltresCatalogue,
    t: Traduire
): AlarmesV2TypeAlarme[] {
    const mots = sansAccentMin(f.texte)
        .split(/\s+/)
        .filter((m) => m !== "")
    const liste = types.filter((ty) => {
        if (f.mesure !== "tous" && mesureDuType(ty, t) !== f.mesure)
            return false
        if (
            f.equipement !== "tous" &&
            !(
                Array.isArray(ty.objet_types) &&
                ty.objet_types.includes(f.equipement)
            )
        )
            return false
        if (mots.length > 0) {
            const cherche = texteCherche(ty, t)
            if (!mots.every((m) => cherche.includes(m))) return false
        }
        return true
    })
    return liste.sort((a, b) => {
        if (f.tri === "a_traiter") {
            const ia = estPourInformation(a) ? 1 : 0
            const ib = estPourInformation(b) ? 1 : 0
            if (ia !== ib) return ia - ib
        }
        return nomDuTypeDuCatalogue(a, t).localeCompare(
            nomDuTypeDuCatalogue(b, t),
            "fr"
        )
    })
}
